import React, { useCallback, useEffect, useState } from "react";
import { useLanguageContext } from "../../Context/LanguageContext";
import { useGlobalSpinnerActionsContext } from "../../Context/GlobalSpinnerContext";
import EquipmentActions from "../../Actions/EquipmentActions";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { Add } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

export default function DeleteEquipment({
  delEquipmentDetails,
  handleDelEquPoPClose,
  isOpenDeletePopOver,
}) {
  const { localization } = useLanguageContext();
  const setGlobalSpinner = useGlobalSpinnerActionsContext();
  const [selOption, setSelOption] = useState("RemoveChild");
  const dropDown = [
    { key: "RemoveChild", label: localization.removeChildren },
    { key: "DeleteChild", label: localization.delChildren },
  ];
  const [scrapReason, setScrapReason] = useState("");
  const [scrapImage, setScrapImage] = useState(null);

  const handleFinalDelete = useCallback(() => {
    if (!scrapReason.trim()) {
      toast.error(localization.scrapReasonRequired);
      return;
    }

    setGlobalSpinner(true);
    EquipmentActions.ScrapEquipment({
      Id: delEquipmentDetails.id,
      ScrappedReason: scrapReason,
      EquipmentPhotos: scrapImage,
      DeleteChildOption: delEquipmentDetails.hasChildren ? selOption : null,
    });

    // Reset state
    setScrapReason("");
    setScrapImage(null);
    handleDelEquPoPClose(true);
  }, [scrapReason, scrapImage, setGlobalSpinner]);

  const handleImageUpload = useCallback(
    (event) => {
      const files = Array.from(event.target.files);
      if (files && files.length > 0) {
        let tempFileArray = [...(scrapImage || [])];
        let filesProcessed = 0;

        files.forEach((file) => {
          const reader = new FileReader();
          reader.onload = () => {
            tempFileArray.push({
              Filename: file.name,
              Filecontent: reader.result,
              Filetyp: file.type,
              FileContentBytes: null,
            });
            filesProcessed++;
            if (filesProcessed === files.length) {
              setScrapImage([...tempFileArray]);
            }
          };
          reader.onerror = () => {
            filesProcessed++;
          };
          reader.readAsDataURL(file);
        });
      }
    },
    [scrapImage],
  );

  const handleCancelSecondDelete = useCallback(() => {
    setScrapReason("");
    setScrapImage(null);
    handleDelEquPoPClose(false);
  }, []);

  const handleDeleteImage = useCallback(
    (index) => {
      const updatedImages = scrapImage.filter((_, i) => i !== index);
      setScrapImage(updatedImages.length > 0 ? updatedImages : null);
    },
    [scrapImage],
  );

  return (
    <Dialog
      open={isOpenDeletePopOver}
      onClose={handleCancelSecondDelete}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #e0e0e0",
          fontWeight: 600,
          fontSize: "1.25rem",
          color: "#333",
          padding: "16px 24px",
        }}
      >
        {localization.confirmDelete}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "28px",
          backgroundColor: "#fafafa",
        }}
      >
        <Alert
          severity="warning"
          sx={{
            mt: 3,
            mb: 3,
            borderRadius: 1,
            "& .MuiAlert-icon": {
              fontSize: "24px",
            },
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          {localization.actionCannotBeUndone}
        </Alert>
        {delEquipmentDetails.hasChildren && (
          <div className="form-group">
            <label className="form-label"> {localization.header}</label>
            <select
              className="form-control"
              id="deleteOption"
              name="deleteOption"
              value={selOption}
              onChange={(e) => {
                setSelOption(e.target.value);
              }}
            >
              {dropDown.map((row, index) => {
                return (
                  <option key={index} value={row.key}>
                    {row.label}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <TextField
          className="mb-3"
          fullWidth
          label={localization.scrapReason}
          multiline
          rows={3}
          value={scrapReason}
          onChange={(e) => setScrapReason(e.target.value)}
          required
        />
        <Box
          sx={{
            mb: 2,
            padding: "16px",
            backgroundColor: "#fff",
            borderRadius: 1,
            border: "1px dashed #ccc",
          }}
        >
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="scrap-image-upload"
            multiple
            type="file"
            onChange={handleImageUpload}
          />
          <label htmlFor="scrap-image-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Add />}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                padding: "8px 16px",
                borderRadius: 1,
                borderColor: "#1976d2",
                color: "#1976d2",
                "&:hover": {
                  backgroundColor: "#e3f2fd",
                  borderColor: "#1565c0",
                },
              }}
            >
              {localization.uploadImage}
            </Button>
          </label>
          {scrapImage && scrapImage.length > 0 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {scrapImage.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: "8px 12px",
                    backgroundColor: "#e3f2fd",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1976d2",
                      fontWeight: 500,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {image.Filename}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => handleDeleteImage(index)}
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "4px 12px",
                      fontSize: "12px",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "#ffebee",
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          backgroundColor: "#f5f5f5",
          borderTop: "1px solid #e0e0e0",
          gap: 1,
        }}
      >
        <Button
          onClick={handleCancelSecondDelete}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            padding: "8px 20px",
            color: "#666",
            "&:hover": {
              backgroundColor: "#e0e0e0",
            },
          }}
        >
          {localization.cancelbutton}
        </Button>
        <Button
          onClick={handleFinalDelete}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            padding: "8px 20px",
            backgroundColor: "#d32f2f",
            boxShadow: "0 2px 8px rgba(211, 47, 47, 0.3)",
            "&:hover": {
              backgroundColor: "#c62828",
              boxShadow: "0 4px 12px rgba(211, 47, 47, 0.4)",
            },
          }}
        >
          {localization.delete}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
